<template>
  <div
    v-if="visible"
    class="gtm-overlay"
    @keydown.esc="close"
    tabindex="0"
  >
    <div class="gtm-panel">
      <div class="gtm-searchwrap">
        <i class="fa-solid fa-magnifying-glass gtm-search-icon"></i>
        <input
          type="text"
          class="gtm-search-input"
          placeholder="Buscar na lista..."
          v-model.trim="query"
        />
        <button
          class="gtm-btn-circle"
          @click="close"
          title="Fechar"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="gtm-actions-row">
        <button
          class="gtm-create-btn"
          @click="openCreateModel"
        >
          <i class="fas fa-plus-circle"></i>
          <span>Criar Modelo</span>
        </button>
      </div>

      <div class="gtm-list-wrap">
        <div class="gtm-table-header">
          <div class="gtm-col-name">Nome</div>
          <div class="gtm-col-actions">Ações</div>
        </div>

        <div class="gtm-results">
          <div
            v-if="!filteredTemplates.length"
            class="gtm-empty"
          >
            <i class="fa-solid fa-folder-open gtm-empty-icon"></i>
            Nenhum modelo encontrado.
          </div>

          <div
            v-for="(t, idx) in filteredTemplates"
            :key="t.id || idx"
            class="gtm-row"
            :class="{ active: t.id === editingId }"
          >
            <div class="gtm-col-name gtm-row-name">
              <template v-if="editingId === t.id">
                <div class="gtm-input-wrap">
                  <input
                    class="gtm-edit-input"
                    v-model.trim="editTitle"
                    @click.stop
                    @keydown.enter.prevent="save(t)"
                  />
                </div>
              </template>

              <template v-else>
                <span
                  class="gtm-template-pill gtm-pill-locked"
                  @click="importTemplate(t)"
                  :title="t.name"
                >
                  <i
                    v-if="isLockedTemplate(t)"
                    class="fa-solid fa-lock gtm-lock-icon"
                  ></i>
                  {{ t.name }}
                </span>
              </template>
            </div>

            <div class="gtm-col-actions gtm-row-actions">
              <button
                class="gtm-btn-circle gtm-icon-btn"
                @click.stop="importTemplate(t)"
                title="Importar"
              >
                <i class="fa-solid fa-file-import icon-import"></i>
              </button>

              <button
                v-if="!isLockedTemplate(t)"
                class="gtm-btn-circle gtm-icon-btn"
                @click.stop="openEditModel(t)"
                title="Editar"
              >
                <i class="fa-regular fa-pen-to-square icon-edit"></i>
              </button>

              <button
                v-if="editingId === t.id"
                class="gtm-btn-circle gtm-icon-btn"
                @click.stop="save(t)"
                :disabled="!editTitle"
                title="Salvar"
              >
                <i class="fa-solid fa-floppy-disk icon-save"></i>
              </button>

              <button
                v-if="!isLockedTemplate(t)"
                class="gtm-btn-circle gtm-icon-btn danger"
                @click.stop="remove(t)"
                title="Remover"
              >
                <i class="fa-solid fa-trash icon-remove"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="gtm-backdrop"
      @click="close"
    ></div>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from "vuex";

export default {
  name: "GroupTemplateList",
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    closeOnSave: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      templates: [],
      editingId: null,
      editTitle: "",
      internalTemplate: "",
      query: "",
      loading: false,
    };
  },

  created() {
    this.getAll();
  },

  computed: {
    ...mapState("rent", ["groupTemplates"]),
    filteredTemplates() {
      const q = (this.query || "").toLowerCase();
      if (!q) return this.templates;
      return this.templates.filter((t) =>
        (t.name || "").toLowerCase().includes(q)
      );
    },
  },

  methods: {
    ...mapMutations("rent", ["setGroupTemplates"]),
    ...mapActions("generic", ["getApi", "putApi", "deleteApi"]),

    isLockedTemplate(t) {
      if (!t) return false;
      return (
        !!t.isSystem ||
        t.id === "system-group-subgroup-example" ||
        t.id === "system-hash-group-template"
      );
    },

    getAll() {
      this.loading = true;
      const params = { url: "/api/v1/rental/group-template/get-all" };
      this.getApi(params).then((response) => {
        this.setGroupTemplates(response.content.data);
        this.syncTemplatesFromStore();
        this.loading = false;
      });
    },

    syncTemplatesFromStore() {
      this.templates = Array.isArray(this.groupTemplates)
        ? JSON.parse(JSON.stringify(this.groupTemplates))
        : [];
    },

    close() {
      this.$emit("update:visible", false);
      this.editingId = null;
      this.editTitle = "";
      this.query = "";
    },

    rowClick(t) {
      if (this.isLockedTemplate(t)) return;
      this.editingId = t.id;
      this.editTitle = t.name;
    },

    save(t) {
      if (this.isLockedTemplate(t)) return;
      if (!this.editTitle) return;

      this.loading = true;
      const params = {
        url: "/api/v1/rental/group-template/update",
        obj: {
          id: t.id,
          name: this.editTitle,
          json: t.json,
        },
      };
      this.putApi(params).then((response) => {
        if (response.success) {
          this.editingId = null;
          this.editTitle = "";
          this.getAll();
        }
        this.loading = false;
      });
    },

    remove(t) {
      if (this.isLockedTemplate(t)) return;
      this.loading = true;
      const params = {
        url: `/api/v1/rental/group-template/delete?id=${t.id}`,
        obj: {},
      };
      this.deleteApi(params).then((response) => {
        if (response.success) {
          if (this.editingId === t.id) {
            this.editingId = null;
            this.editTitle = "";
          }
          this.getAll();
        }
        this.loading = false;
      });
    },

    importTemplate(t) {
      const obj = {
        title: t.name || "",
        template:
          typeof t.json === "string" ? t.json : JSON.stringify(t.json || {}),
      };
      this.$emit("input", obj);
      this.$emit("update:visible", false);
    },

    openEditModel(t) {
      if (this.isLockedTemplate(t)) return;
      if (!t || !t.id) return;
      this.$emit("edit-model", {
        id: t.id,
        name: t.name || "",
        json:
          typeof t.json === "string" ? t.json : JSON.stringify(t.json || []),
      });
      this.$emit("update:visible", false);
    },

    openCreateModel() {
      this.$emit("create-model");
      this.$emit("update:visible", false);
    },
  },

  watch: {
    visible(v) {
      if (v) {
        this.getAll();
        this.$nextTick(() => {
          const input = this.$el.querySelector(".gtm-search-input");
          input && input.focus();
        });
      }
    },

    groupTemplates: {
      deep: true,
      handler() {
        if (!this.visible) return;
        this.syncTemplatesFromStore();
      },
    },

    value: {
      immediate: true,
      deep: true,
      handler(v) {
        if (v && typeof v === "object") {
          this.internalTemplate =
            typeof v.template === "string"
              ? v.template
              : this.internalTemplate || "";
        }
      },
    },
  },
};
</script>

<style scoped>
.gtm-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
}

.gtm-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
}

.gtm-panel {
  position: relative;
  z-index: 1;
  width: min(720px, 92vw);
  background: white;
  border-radius: 16px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.gtm-searchwrap {
  margin-top: 2px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 12px;
  box-shadow: 0 0 0 1px #e2e8f0 inset;
}

.gtm-search-icon {
  color: #64748b;
  margin-top: 8px;
}

.gtm-search-input {
  border: none;
  outline: none;
  background: transparent;
  height: 28px;
  font-size: 13px;
  color: #0f172a;
  margin-top: 2px;
}

.gtm-actions-row {
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
}

.gtm-create-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 7px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #577696;
  border-color: #577696;
  color: #fff;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
}

.gtm-create-btn:hover {
  background: #355472;
}

.gtm-btn-circle {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  display: grid;
  place-items: center;
  border: none;
  background: #eef2f7;
  cursor: pointer;
}

.gtm-btn-circle:hover {
  background: #e2e8f0;
}

.gtm-btn-circle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-save {
  color: #577696;
}

.icon-import {
  color: #009183;
}

.icon-edit {
  color: #8d8f91;
}

.icon-remove {
  color: #ef4444;
}

.gtm-list-wrap {
  margin-top: 14px;
}

.gtm-table-header {
  display: flex;
  padding: 10px 16px;
  background: #f9fafb;
  border: 1px solid #e2e8f0;
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.gtm-results {
  max-height: 360px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
}

.gtm-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 16px;
  color: #94a3b8;
  font-size: 14px;
  background: #f8fafc;
}

.gtm-empty-icon {
  font-size: 16px;
  color: #cbd5e1;
}

.gtm-col-name {
  flex: 1;
  min-width: 0;
}

.gtm-col-actions {
  width: 130px;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.gtm-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s ease;
}

.gtm-row:last-child {
  border-bottom: none;
}

.gtm-row.active {
  background: #e8f0fe;
}

.gtm-row-name {
  display: flex;
  align-items: center;
  min-width: 0;
}

.gtm-template-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background 0.15s;
}

.gtm-pill-locked {
  background: #f1f5f9;
  color: #64748b;
}

.gtm-pill-locked:hover {
  background: #f1f5f9;
}

.gtm-lock-icon {
  font-size: 11px;
  color: #94a3b8;
}

.gtm-row-actions {
  flex-shrink: 0;
}

.gtm-input-wrap {
  width: 100%;
}

.gtm-edit-input {
  width: 100%;
  padding: 6px 10px;
  font-size: 13px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
}

.gtm-edit-input:focus {
  outline: none;
  border-color: #94a3b8;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}
</style>
